import { useId, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/misc';
import { COMMON_COUNTRY_CODES, COUNTRIES, citiesFor } from '@/lib/geo';
import { cn } from '@/lib/utils';

/** Sentinel for the "Other…" city option. It never reaches the form state. */
const OTHER = '__other__';

const COMMON_COUNTRIES = COMMON_COUNTRY_CODES.flatMap((code) => {
  const country = COUNTRIES.find((candidate) => candidate.code === code);
  return country ? [country] : [];
});

type CountryCityFieldsProps = {
  country: string;
  city: string;
  /** Receives only the keys that changed. Picking a country also clears the city. */
  onChange: (patch: { country?: string; city?: string }) => void;
  countryLabel?: string;
  cityLabel?: string;
  required?: boolean;
  /** Applied to the wrapping grid — pass "sm:col-span-2" inside a two-column form. */
  className?: string;
};

/**
 * Country dropdown plus a city dropdown that follows it. Both hand back plain
 * names, which is what the API already stores (see lib/geo.ts), so an old
 * record with a typed country still opens with its value selected.
 */
export function CountryCityFields({
  country,
  city,
  onChange,
  countryLabel = 'Country',
  cityLabel = 'City',
  required,
  className,
}: CountryCityFieldsProps) {
  const id = useId();
  const cities = citiesFor(country);
  const countryKnown = country === '' || COUNTRIES.some((candidate) => candidate.name === country);
  const cityListed = cities.includes(city);

  // "Other…" is sticky: once chosen the free-text box stays while it is empty,
  // and a stored city we do not list opens in it straight away.
  const [otherChosen, setOtherChosen] = useState(() => city !== '' && !cityListed);
  const showOther = otherChosen || (city !== '' && !cityListed);

  const pickCountry = (next: string) => {
    setOtherChosen(false);
    onChange({ country: next, city: '' });
  };

  const pickCity = (next: string) => {
    if (next === OTHER) {
      setOtherChosen(true);
      onChange({ city: '' });
      return;
    }
    setOtherChosen(false);
    onChange({ city: next });
  };

  return (
    <div className={cn('grid gap-4 sm:grid-cols-2', className)}>
      <div className="space-y-1.5">
        <Label htmlFor={`${id}-country`}>{countryLabel}</Label>
        <Select
          id={`${id}-country`}
          value={country}
          required={required}
          onChange={(event) => pickCountry(event.target.value)}
        >
          <option value="">Select country</option>
          {!countryKnown && <option value={country}>{country}</option>}
          <optgroup label="Common">
            {COMMON_COUNTRIES.map((candidate) => (
              <option key={candidate.code} value={candidate.name}>
                {candidate.name}
              </option>
            ))}
          </optgroup>
          <optgroup label="All countries">
            {COUNTRIES.map((candidate) => (
              <option key={candidate.code} value={candidate.name}>
                {candidate.name}
              </option>
            ))}
          </optgroup>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`${id}-city`}>{cityLabel}</Label>
        {cities.length === 0 ? (
          <Input
            id={`${id}-city`}
            value={city}
            required={required}
            onChange={(event) => onChange({ city: event.target.value })}
          />
        ) : (
          <>
            <Select
              id={`${id}-city`}
              value={showOther ? OTHER : city}
              required={required}
              onChange={(event) => pickCity(event.target.value)}
            >
              <option value="">Select city</option>
              {cities.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
              <option value={OTHER}>Other…</option>
            </Select>
            {showOther && (
              <Input
                aria-label={`${cityLabel} (other)`}
                value={city}
                required={required}
                placeholder="Type the city"
                onChange={(event) => onChange({ city: event.target.value })}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
